/**
 * Phase-0 diagnostic ring buffer for the recorded-video pipeline.
 *
 * Answers one question with data instead of reasoning: **when a short-lived
 * visual state exists in the page, does it reach the encoded mp4 — and if not,
 * which stage discarded it?** There are two independent discard points, and
 * timestamps alone cannot distinguish them:
 *
 * 1. **Bus throttle** — `cdp-screencast.ts` `handleFrame` skips a subscriber
 *    whose `minIntervalMs` has not elapsed. Change-driven, so survival is
 *    phase-dependent rather than impossible.
 * 2. **Encoder bucket collision** — `screencast-file-encoder.ts` `pushFrame`
 *    quantizes to `floor(elapsed * fps)` and only flushes when the bucket
 *    advances. Two accepted frames inside one bucket means the earlier buffer
 *    is *overwritten and never written to ffmpeg*, so the slot shows whichever
 *    frame arrived last.
 *
 * Recording a content digest per frame is load-bearing: timestamps prove
 * throttle phase, but only the digest proves *which visual state* the surviving
 * frame actually contains.
 *
 * **Off by default and allocation-free when off** — every entry point is
 * guarded by a plain boolean read, so this costs one branch on the frame hot
 * path in normal operation. Entries are buffered in memory and flushed by the
 * caller *after* the interaction, so tracing never perturbs the timing it is
 * measuring.
 *
 * This module is a measurement gate, not a product surface. If repeated trials
 * do not reproduce the ordering problem, the anticipatory-cue work stops here.
 *
 * @module screencast-trace
 */

import { createHash } from 'node:crypto';

/** Stages that can observe or discard a frame, plus cue/action markers. */
export type ScreencastTraceKind =
  /** A frame arrived from CDP and the bus made a per-subscriber accept/drop call. */
  | 'bus_frame'
  /** The file encoder received an accepted frame and made a bucket decision. */
  | 'encoder_push'
  /** A pre-action cue was requested for an element or point. */
  | 'cue_start'
  /** The cue overlay reported itself rendered in the page. */
  | 'cue_rendered'
  /** The cue was skipped (offscreen, null box, aborted, unsupported target). */
  | 'cue_skipped'
  /** Control returned to the driver and the real Playwright action is about to dispatch. */
  | 'action_dispatch'
  /** The real Playwright action settled. */
  | 'action_settled';

/** One buffered observation. Field meaning depends on {@link kind}. */
export interface ScreencastTraceEntry {
  kind: ScreencastTraceKind;
  /** Monotonic ms since process start (`performance.now()`), not wall clock. */
  at: number;
  /** Bus subscriber id, for `bus_frame`. */
  subscriberId?: number;
  /** Whether the bus delivered this frame to the subscriber (`bus_frame`). */
  accepted?: boolean;
  /** Ms since this subscriber's previous delivery (`bus_frame`). */
  sinceLastDeliveryMs?: number;
  /** The throttle the decision was made against (`bus_frame`). */
  minIntervalMs?: number;
  /** Quantized encoder slot, `floor(elapsed * fps)` (`encoder_push`). */
  frameNumber?: number;
  /** Slot of the previously buffered frame (`encoder_push`). */
  previousFrameNumber?: number;
  /** Duplicate frames emitted to cover the gap (`encoder_push`). */
  gapFillCount?: number;
  /**
   * True when this push landed in the same slot as the previous one, so the
   * previously buffered frame was discarded without ever reaching ffmpeg
   * (`encoder_push`). This is the silent-loss signal.
   */
  overwrotePreviousFrame?: boolean;
  /** Short digest of the JPEG bytes — identifies the visual state. */
  digest?: string;
  /** Byte length of the frame. */
  bytes?: number;
  /** Free-form label: cue target kind, skip reason, action method name. */
  label?: string;
}

const MAX_ENTRIES = 4096;

let enabled = false;
let buffer: ScreencastTraceEntry[] = [];
/** Write cursor for the ring; wraps at {@link MAX_ENTRIES}. */
let cursor = 0;
let wrapped = false;
let dropped = 0;

/**
 * Enable or disable tracing. Disabling clears the buffer so a later trial can
 * never inherit entries from an earlier one.
 */
export function setScreencastTraceEnabled(on: boolean): void {
  enabled = on;
  if (!on) resetScreencastTrace();
}

/** Whether tracing is currently on. Cheap enough to call on the hot path. */
export function isScreencastTraceEnabled(): boolean {
  return enabled;
}

/** Drop all buffered entries and counters. */
export function resetScreencastTrace(): void {
  buffer = [];
  cursor = 0;
  wrapped = false;
  dropped = 0;
}

/**
 * Buffer one observation. No-op when tracing is off.
 *
 * Never throws: a diagnostic must not be able to break a recording. The ring
 * overwrites oldest-first and counts the loss so a flush can report whether the
 * window was complete.
 */
export function traceScreencast(entry: Omit<ScreencastTraceEntry, 'at'>): void {
  if (!enabled) return;
  try {
    const record: ScreencastTraceEntry = { ...entry, at: performance.now() };
    if (buffer.length < MAX_ENTRIES) {
      buffer.push(record);
      cursor = buffer.length % MAX_ENTRIES;
      return;
    }
    if (!wrapped) wrapped = true;
    dropped++;
    buffer[cursor] = record;
    cursor = (cursor + 1) % MAX_ENTRIES;
  } catch {
    /* diagnostics must never break the run */
  }
}

/**
 * Digest JPEG bytes so a frame in the trace can be matched to a visual state.
 * Returns `undefined` when tracing is off so callers never pay for the hash.
 */
export function traceDigest(jpeg: Buffer): string | undefined {
  if (!enabled) return undefined;
  try {
    return createHash('sha1').update(new Uint8Array(jpeg)).digest('hex').slice(0, 12);
  } catch {
    return undefined;
  }
}

/** A flushed trace window, in chronological order. */
export interface ScreencastTraceFlush {
  entries: ScreencastTraceEntry[];
  /** Entries evicted by ring wraparound — non-zero means the window is truncated. */
  droppedEntries: number;
}

/**
 * Read the buffered window in chronological order and clear it. Call *after*
 * the interaction under test, never during.
 */
export function flushScreencastTrace(): ScreencastTraceFlush {
  const entries = wrapped ? [...buffer.slice(cursor), ...buffer.slice(0, cursor)] : [...buffer];
  const droppedEntries = dropped;
  resetScreencastTrace();
  return { entries, droppedEntries };
}
